import { Alert, AlertTitle, Box, Chip, Stack, Typography } from "@mui/material";
import type { PackageOverlap, PackageOverlapReport } from "../http/foyer";

/**
 * "You are about to make the ninth copy of `basic-sweden`." (`ENT-3`,
 * `docs/content-model-v2.md` §8.)
 *
 * ## Why this component exists at all
 *
 * The portal held 244 packages over 93 distinct names — `basic-sweden` NINE
 * times — because there was nowhere to say *"same content, different customer,
 * different price"*, so an operator copied the package. `ENT-3` deleted the
 * importer so those rows are never recreated, but **deleting an importer does
 * not stop a human authoring the ninth copy by hand.** The server computes the
 * overlap; this is the half the operator can actually see, and without it the
 * guard is a field nobody reads.
 *
 * ## It warns, it never blocks
 *
 * §8 is explicit: *"They may proceed — having seen they are making the 9th copy
 * of `basic-sweden`."* A hard refusal would only ever fight the legitimate
 * case. So there is no confirmation step here and no disabled Save — the
 * operator is told, and decides.
 *
 * ## The wording is chosen from the numbers, not fixed
 *
 * Two shapes need opposite advice, and showing one percentage for both is how a
 * warning gets ignored:
 *
 *  - **Contained** (`onlyHere === 0`) — everything here is already granted
 *    elsewhere. The answer is to compose a Product from the existing package,
 *    or, when the difference is a few titles, to carve those out with excludes.
 *  - **Near-copy** (`onlyHere > 0`) — mostly the same, with a little of its own.
 *    The answer is additive slicing: `basic` and `basic + cnn`, never
 *    `basic − cnn`.
 */

function pct(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/** The named titles, capped server-side. Nothing is invented here. */
function Sample({ ids }: { ids: string[] }) {
  if (ids.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
      {ids.map((id) => (
        <Chip key={id} label={id} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

function One({ o }: { o: PackageOverlap }) {
  const contained = o.onlyHere === 0;
  // A carve-out worth naming: everything here is already there, and there is a
  // short, nameable list of what the other one has on top. That is the
  // "basic-sweden minus SVT 2" case, and naming the titles is the whole value.
  const carveOut = contained && o.onlyThere > 0;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {contained
          ? `Everything in this package is already in “${o.name}”.`
          : `${pct(o.share)} of this package is already in “${o.name}”.`}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {o.shared} of {o.shared + o.onlyHere} title(s) shared · “{o.name}” holds{" "}
        {o.theirs}
        {o.onlyHere > 0 ? ` · ${o.onlyHere} only here` : ""}
        {o.onlyThere > 0 ? ` · ${o.onlyThere} only there` : ""}
      </Typography>

      {carveOut && o.onlyThere <= o.onlyThereSample.length ? (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            This is “{o.name}” without:
          </Typography>
          <Sample ids={o.onlyThereSample} />
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            That is an <strong>exclude</strong> on “{o.name}”, not a second
            package. One package to maintain, and the difference is written down
            instead of hidden in a copy.
          </Typography>
        </Box>
      ) : contained ? (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          Nothing here is unique. Consider granting “{o.name}” from a Product
          instead of keeping a second package — when the provider adds a
          channel, you would otherwise have to remember both.
        </Typography>
      ) : (
        <Box sx={{ mt: 0.75 }}>
          {o.onlyHereSample.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Only in this package:
              </Typography>
              <Sample ids={o.onlyHereSample} />
            </>
          ) : null}
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            Consider a Product granting “{o.name}” <em>plus</em> a small package
            holding only the difference, rather than a near-duplicate of it.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function PackageOverlapNotice({
  report,
  onDismiss,
}: {
  report: PackageOverlapReport | null;
  onDismiss?: () => void;
}) {
  if (!report || report.overlaps.length === 0) return null;

  return (
    <Alert
      severity="warning"
      onClose={onDismiss}
      sx={{ "& .MuiAlert-message": { width: "100%" } }}
    >
      <AlertTitle>
        {report.overlaps.length === 1
          ? "This package is a near-copy of another one"
          : `This package is a near-copy of ${report.overlaps.length} others`}
      </AlertTitle>

      <Typography variant="body2" color="text.secondary">
        Nothing is blocked — you can keep it. Worth a look first: nine copies of
        “Basic Sweden” is how the old catalogue reached 244 packages, and copies
        drift apart silently.
      </Typography>

      {report.overlaps.map((o) => (
        <One key={o.id} o={o} />
      ))}

      {report.truncated || report.candidatesTruncated ? (
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ mt: 1.5 }}
        >
          {report.truncated
            ? "Some packages are larger than the preview cap, so these shares are a floor. "
            : ""}
          {report.candidatesTruncated
            ? "Not every package was compared, so there may be more."
            : ""}
        </Typography>
      ) : null}
    </Alert>
  );
}
